/**
 * <PageLead/> — the orienting strip at the top of every authenticated route.
 *
 * One pure-render component with six slots: `eyebrow`, `title`, `subtitle`,
 * `state`, `primary`, `secondary`. No data fetching, no router, no context,
 * no internal state — pages compose the data themselves and pass ReactNodes
 * into the slots.
 *
 * Why it exists
 * -------------
 * The page-orientation audit (`docs/design/page-orientation-audit.md`,
 * Section 1) found the same H1 className copy-pasted in 12+ component
 * headers, with subtly different right-cluster spacing each time. PageLead
 * absorbs that pattern so every page reads the same:
 *
 *   <eyebrow uppercase>
 *   <H1 title>            <state pill>  <secondary>  <primary>
 *   <subtitle>
 *
 * The H1 className matches what existing pages already use
 * (`text-3xl font-semibold tracking-tight text-fg sm:text-4xl`) so visual
 * weight does not change when PL2-PL5 migrate routes onto it.
 *
 * Mobile (≤640px / Tailwind sm:) stacks the right cluster below the title
 * cluster. The state pill stays before the buttons; if the buttons need
 * more room, `flex-wrap` lets them flow to a second line.
 */

import type { HTMLAttributes, ReactNode } from "react";

import { cn } from "../../lib/cn";

export interface PageLeadProps extends Omit<HTMLAttributes<HTMLElement>, "title"> {
  /** Optional small uppercase eyebrow above the title (e.g. "Cycle", "Team"). */
  eyebrow?: ReactNode;
  /** Main page title (rendered as an H1). Required — every page has one. */
  title: ReactNode;
  /** One-sentence explanation of the page (founder voice). Optional. */
  subtitle?: ReactNode;
  /** State pill — typically a <Badge/>. Can be any ReactNode. */
  state?: ReactNode;
  /** Primary action — typically a <Button/>. Can be any ReactNode. */
  primary?: ReactNode;
  /** Secondary action — typically a <Button variant="secondary"/>. Optional. */
  secondary?: ReactNode;
  /** Optional className passthrough for layout tweaks on the root <header>. */
  className?: string;
}

export function PageLead({
  eyebrow,
  title,
  subtitle,
  state,
  primary,
  secondary,
  className,
  ...rest
}: PageLeadProps) {
  const hasRightCluster = Boolean(state || primary || secondary);

  return (
    <header className={cn("mb-6 flex flex-col gap-3", className)} {...rest}>
      {eyebrow ? (
        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-accent-fg">
          {eyebrow}
        </p>
      ) : null}

      <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-3">
          <h1 className="font-display text-3xl font-semibold tracking-tight text-fg sm:text-4xl">
            {title}
          </h1>
        </div>

        {hasRightCluster ? (
          <div className="flex flex-shrink-0 flex-wrap items-center gap-2">
            {state}
            {secondary}
            {primary}
          </div>
        ) : null}
      </div>

      {subtitle ? <p className="max-w-2xl text-sm text-fg-muted">{subtitle}</p> : null}
    </header>
  );
}
